import { Injectable } from '@angular/core';

export interface ColorSettings {
  primaryColor: string;
  secondaryColor: string;
  tertiaryColor: string;
  backgroundColor: string;
}

export interface ColorTheme {
  name: string;
  primary: string;
  secondary: string;
  tertiary: string;
  background: string;
}

/**
 * Service responsible for managing color themes and CSS custom property application.
 * Extracted from SearchComponent to reduce component complexity.
 */
@Injectable({
  providedIn: 'root',
})
export class ColorThemeService {
  private readonly COLOR_SETTINGS_KEY = 'drawfiend-color-settings';
  readonly DEFAULT_PRIMARY_COLOR = '#bb86fc';
  readonly DEFAULT_SECONDARY_COLOR = '#000000';
  readonly DEFAULT_TERTIARY_COLOR = '#ff9500';
  readonly DEFAULT_BACKGROUND_COLOR = '#000000';

  readonly colorThemes: ColorTheme[] = [
    {
      name: 'OLED Dark (Pure Black) (Default)',
      primary: '#bb86fc',
      secondary: '#000000',
      tertiary: '#ff9500',
      background: '#000000'
    },
    {
      name: 'Classic (Gold & Blue)',
      primary: '#ffffff',
      secondary: '#152f56',
      tertiary: '#e8c446',
      background: '#020f23'
    },
    {
      name: 'Sunset (Orange & Purple)',
      primary: '#ff6b35',
      secondary: '#4a148c',
      tertiary: '#ff8f65',
      background: '#0d0d0d'
    },
    {
      name: 'Ocean (Teal & Navy)',
      primary: '#26a69a',
      secondary: '#1a237e',
      tertiary: '#4db6ac',
      background: '#0a1929'
    },
    {
      name: 'Forest (Green & Brown)',
      primary: '#66bb6a',
      secondary: '#3e2723',
      tertiary: '#81c784',
      background: '#1b1b1b'
    },
    {
      name: 'Neon (Pink & Dark)',
      primary: '#e91e63',
      secondary: '#212121',
      tertiary: '#f06292',
      background: '#0f0f0f'
    },
    {
      name: 'Cyberpunk (Cyan & Purple)',
      primary: '#00bcd4',
      secondary: '#4a148c',
      tertiary: '#26c6da',
      background: '#0d0d0d'
    },
    {
      name: 'Ember (Red & Charcoal)',
      primary: '#f44336',
      secondary: '#263238',
      tertiary: '#ef5350',
      background: '#1a1a1a'
    },
    {
      name: 'Royal (Purple & Gold)',
      primary: '#9c27b0',
      secondary: '#1a237e',
      tertiary: '#ba68c8',
      background: '#0a0a0a'
    }
  ];

  /**
   * Load color settings from localStorage
   */
  loadColorSettings(): ColorSettings {
    try {
      const saved = localStorage.getItem(this.COLOR_SETTINGS_KEY);
      if (saved) {
        const settings = JSON.parse(saved);
        return {
          primaryColor: settings.primaryColor || this.DEFAULT_PRIMARY_COLOR,
          secondaryColor: settings.secondaryColor || this.DEFAULT_SECONDARY_COLOR,
          tertiaryColor: settings.tertiaryColor || this.DEFAULT_TERTIARY_COLOR,
          backgroundColor: settings.backgroundColor || this.DEFAULT_BACKGROUND_COLOR,
        };
      }
    } catch (error) {
      console.warn('Failed to load color settings:', error);
    }

    return this.getDefaultColors();
  }

  /**
   * Save color settings to localStorage
   */
  saveColorSettings(primaryColor: string, secondaryColor: string, tertiaryColor: string, backgroundColor: string): void {
    try {
      const settings = { primaryColor, secondaryColor, tertiaryColor, backgroundColor };
      localStorage.setItem(this.COLOR_SETTINGS_KEY, JSON.stringify(settings));
      console.log('Color settings saved:', settings);
    } catch (error) {
      console.warn('Failed to save color settings:', error);
    }
  }

  /**
   * Apply color settings to CSS custom properties
   */
  applyColorSettings(primaryColor: string, secondaryColor: string, tertiaryColor: string, backgroundColor: string): void {
    const root = document.documentElement;

    const primaryRgb = this.hexToRgb(primaryColor);
    const secondaryRgb = this.hexToRgb(secondaryColor);
    const tertiaryRgb = this.hexToRgb(tertiaryColor);

    if (primaryRgb && secondaryRgb && tertiaryRgb) {
      root.style.setProperty('--primary-color', primaryColor);
      root.style.setProperty('--primary-rgb', `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`);
      root.style.setProperty('--secondary-color', secondaryColor);
      root.style.setProperty('--secondary-rgb', `${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}`);
      root.style.setProperty('--tertiary-color', tertiaryColor);
      root.style.setProperty('--tertiary-rgb', `${tertiaryRgb.r}, ${tertiaryRgb.g}, ${tertiaryRgb.b}`);

      root.style.setProperty('--bg-primary', backgroundColor);

      root.style.setProperty('--primary-dark', this.darkenColor(primaryColor, 0.1));
      root.style.setProperty('--primary-light', this.lightenColor(primaryColor, 0.1));
      root.style.setProperty('--secondary-dark', this.darkenColor(secondaryColor, 0.1));
      root.style.setProperty('--secondary-light', this.lightenColor(secondaryColor, 0.1));
      root.style.setProperty('--tertiary-dark', this.darkenColor(tertiaryColor, 0.1));
      root.style.setProperty('--tertiary-light', this.lightenColor(tertiaryColor, 0.1));

      console.log('Color settings applied to CSS:', { primaryColor, secondaryColor, tertiaryColor, backgroundColor });
    }
  }

  /**
   * Get default color settings
   */
  getDefaultColors(): ColorSettings {
    return {
      primaryColor: this.DEFAULT_PRIMARY_COLOR,
      secondaryColor: this.DEFAULT_SECONDARY_COLOR,
      tertiaryColor: this.DEFAULT_TERTIARY_COLOR,
      backgroundColor: this.DEFAULT_BACKGROUND_COLOR,
    };
  }

  /**
   * Find a theme by name
   */
  findThemeByName(name: string): ColorTheme | undefined {
    return this.colorThemes.find(t => t.name === name);
  }

  /**
   * Convert hex color to RGB object
   */
  hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  }

  /**
   * Darken a hex color by a percentage
   */
  darkenColor(hex: string, percent: number): string {
    const rgb = this.hexToRgb(hex);
    if (!rgb) return hex;

    const factor = 1 - percent;
    const r = Math.round(rgb.r * factor);
    const g = Math.round(rgb.g * factor);
    const b = Math.round(rgb.b * factor);

    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  }

  /**
   * Lighten a hex color by a percentage
   */
  lightenColor(hex: string, percent: number): string {
    const rgb = this.hexToRgb(hex);
    if (!rgb) return hex;

    const factor = percent;
    const r = Math.round(rgb.r + (255 - rgb.r) * factor);
    const g = Math.round(rgb.g + (255 - rgb.g) * factor);
    const b = Math.round(rgb.b + (255 - rgb.b) * factor);

    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  }
}
